'use client';

import { memo, useEffect, useMemo, useRef } from 'react';
import {
  motion,
  useScroll,
  useVelocity,
  useMotionValue,
  useAnimationFrame,
  type MotionValue,
} from 'framer-motion';
import { PortableText } from 'next-sanity';
import { CTAButton } from '@/shadcn/ui/button';
import { richTextComponent } from '@/sanity/sanityPortableText';
import type { ITestimonialsSectionData } from './TestimonialsSection';
import type { ITestimonialCard } from '@/sanity/lib/definitions';
import ScrollingBanner from '../ScrollingBanner';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';
import { TrustpilotMicroCombo } from '@/components/global/TrustpilotWidgets';

const REPEATS = 4;
const BASE_PX_PER_FRAME = 1.5;
const GAP_PX = 24; // 3rem @ 1rem=8px
const STAR_OPACITIES = [0.1, 0.4, 0.6, 0.8, 1] as const;

function QuoteIcon() {
  return (
    <svg
      className="w-[4rem] h-[3rem] flex-shrink-0"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 24"
      fill="none"
    >
      <path
        fill="#12F0B4"
        d="M18.69 24v-9.855C18.69 6.54 23.663 1.385 30.666 0l1.326 2.868c-3.242 1.223-5.326 4.85-5.326 7.799H32V24zM0 24v-9.855C0 6.54 4.997 1.384 12 0l1.328 2.868C10.084 4.091 8 7.718 8 10.667h5.31V24z"
      />
    </svg>
  );
}

function TrustpilotStars() {
  return (
    <svg
      className="w-[15rem] h-auto"
      xmlns="http://www.w3.org/2000/svg"
      width="122"
      height="23"
      fill="none"
      viewBox="0 0 122 23"
    >
      <g clipPath="url(#tp-clip-v2)">
        <path
          fill="#219653"
          d="M22.875 0H0v22.875h22.875zM47.656 0H24.781v22.875h22.875zM72.438 0H49.561v22.875h22.876zM97.219 0H74.344v22.875h22.875zM122 0H99.125v22.875H122z"
        />
        <path
          fill="#fff"
          d="m11.437 15.417 3.48-.882 1.453 4.48zm8.007-5.79H13.32L11.437 3.86 9.555 9.626H3.43l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM36.219 15.417l3.478-.882 1.454 4.48zm8.006-5.79H38.1L36.218 3.86l-1.882 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM61 15.417l3.479-.882 1.453 4.48zm8.006-5.79h-6.124L61 3.86l-1.883 5.766h-6.124l4.957 3.575-1.883 5.766 4.956-3.574 3.05-2.192zM85.78 15.417l3.48-.882 1.453 4.48zm8.007-5.79h-6.124L85.781 3.86l-1.883 5.766h-6.123l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192zM110.562 15.417l3.479-.882 1.454 4.48zm8.006-5.79h-6.123l-1.883-5.767-1.882 5.766h-6.124l4.956 3.575-1.882 5.766 4.956-3.574 3.05-2.192z"
        />
      </g>
      <defs>
        <clipPath id="tp-clip-v2">
          <path fill="#fff" d="M0 0h122v22.875H0z" />
        </clipPath>
      </defs>
    </svg>
  );
}

const TestimonialCard = memo(function TestimonialCard({
  card,
  theme = 'dark',
  compactMobile = false,
}: {
  card: ITestimonialCard;
  theme?: 'dark' | 'light';
  compactMobile?: boolean;
}) {
  const isLight = theme === 'light';
  return (
    <article
      className={cn(
        'relative flex-shrink-0 w-[46rem] lg:w-[70.875rem] rounded-[0.25rem] overflow-hidden',
        compactMobile && 'max-lg:w-[min(49.75rem,calc(100vw-4rem))]',
        isLight
          ? 'bg-white border border-black/10'
          : 'bg-[#0f2a1e] border border-white/[0.08]',
      )}
    >
      {/* Dot texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage: isLight
            ? 'radial-gradient(circle, rgba(0,0,0,0.6) 1px, transparent 1px)'
            : 'radial-gradient(circle, rgba(255,255,255,0.9) 1px, transparent 1px)',
          backgroundSize: '10px 10px',
        }}
      />

      <div className={cn("relative flex gap-[3rem] lg:gap-[5.875rem] items-start px-[3rem] py-[3rem] lg:px-[7.25rem] lg:py-[4.75rem]", compactMobile && "max-lg:gap-[3rem] max-lg:px-[2.5rem] max-lg:py-[2.5rem]")}>
        <QuoteIcon />

        <div className={cn("flex flex-col gap-[2rem] lg:gap-[2.5rem] flex-1 min-w-0", compactMobile && "max-lg:gap-[1.625rem]")}>
          {card.quote && (
            <div
              className={cn(
                'richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.3] prose-p:tracking-[-0.06rem] prose-p:font-normal prose-p:mb-0 prose-headings:text-[1.75rem] lg:prose-headings:text-[2.25rem] prose-headings:leading-[1.3] prose-headings:mb-0',
                isLight
                  ? 'prose-p:text-black prose-headings:text-black'
                  : 'prose-p:text-white prose-headings:text-white',
              )}
            >
              <PortableText value={card.quote} components={richTextComponent} />
            </div>
          )}

          <div className="flex flex-col gap-[1.5rem]">
            <div className={cn("flex items-center gap-[1.25rem]", compactMobile && "max-lg:hidden")}>
              {STAR_OPACITIES.map((opacity, i) => (
                <span
                  key={i}
                  className="w-[0.75rem] h-[0.75rem] bg-[#003828] flex-shrink-0"
                  style={{ opacity }}
                />
              ))}
            </div>

            <div className="flex items-center gap-[1.5rem] lg:gap-[1.875rem]">
              {card.name && (
                <span
                  className={cn(
                    'font-geist-mono font-semibold text-[1.5rem] lg:text-[1.75rem] leading-[1.33] uppercase whitespace-nowrap',
                    isLight ? 'text-black' : 'text-white',
                  )}
                >
                  {card.name}
                </span>
              )}
              <div
                className={cn(
                  'w-px h-[2.25rem] flex-shrink-0',
                  isLight ? 'bg-black/20' : 'bg-white/20',
                )}
              />
              {card.role && (
                <span
                  className={cn(
                    'font-geist-mono font-semibold text-[1.25rem] lg:text-[1.5rem] leading-[1.33] uppercase whitespace-nowrap',
                    isLight ? 'text-black/[0.43]' : 'text-white/[0.43]',
                  )}
                >
                  {card.role}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
});

function MarqueeRow({
  cards,
  direction,
  scrollVelocity,
  theme = 'dark',
  compactMobile = false,
}: {
  cards: ITestimonialCard[];
  direction: 1 | -1;
  scrollVelocity: MotionValue<number>;
  theme?: 'dark' | 'light';
  compactMobile?: boolean;
}) {
  const x = useMotionValue(0);
  const firstCopyRef = useRef<HTMLDivElement>(null);
  const singleWidthRef = useRef(0);
  const initialized = useRef(false);
  const isPaused = useRef(false);

  const repeated = useMemo(
    () => Array.from({ length: REPEATS }, () => cards),
    [cards],
  );

  // Cache singleWidth — avoids offsetWidth reflow every animation frame
  useEffect(() => {
    const update = () => {
      if (firstCopyRef.current) {
        singleWidthRef.current = firstCopyRef.current.offsetWidth + GAP_PX;
      }
    };
    initialized.current = false;
    x.set(0);
    update();
    const ro = new ResizeObserver(update);
    if (firstCopyRef.current) ro.observe(firstCopyRef.current);
    return () => ro.disconnect();
  }, [cards.length, x]);

  useAnimationFrame((_, delta) => {
    if (isPaused.current) return;
    const singleWidth = singleWidthRef.current;
    if (singleWidth <= GAP_PX) return;

    if (!initialized.current) {
      initialized.current = true;
      if (direction === 1) {
        x.set(-singleWidth);
        return;
      }
    }

    const vel = scrollVelocity.get();
    const boost = Math.min(Math.abs(vel) * 0.003, 6);
    const speed = BASE_PX_PER_FRAME * (1 + boost) * (delta / 16.67);

    if (direction === -1) {
      let newX = x.get() - speed;
      if (newX < -singleWidth) newX += singleWidth;
      x.set(newX);
    } else {
      let newX = x.get() + speed;
      if (newX > 0) newX -= singleWidth;
      x.set(newX);
    }
  });

  return (
    <div
      className={cn("relative overflow-hidden w-full", compactMobile && "max-lg:flex")}
      style={{
        maskImage:
          'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0%, black 12%, black 88%, transparent 100%)',
      }}
      onMouseEnter={() => {
        isPaused.current = true;
      }}
      onMouseLeave={() => {
        isPaused.current = false;
      }}
    >
      <motion.div
        style={{ x }}
        className="flex gap-[3rem] will-change-transform"
      >
        {repeated.map((copyCards, r) => (
          <div
            key={r}
            ref={r === 0 ? firstCopyRef : undefined}
            className="flex gap-[3rem] flex-shrink-0"
          >
            {copyCards.map((card, i) => (
              <TestimonialCard
                key={`${r}-${i}-${card._key ?? ''}`}
                card={card}
                theme={theme}
                compactMobile={compactMobile}
              />
            ))}
          </div>
        ))}
      </motion.div>
    </div>
  );
}

export default function TestimonialsSectionV2({
  data,
  language,
  className,
  theme = 'dark',
  compactMobile = false,
}: {
  data: ITestimonialsSectionData;
  language?: string;
  className?: string;
  theme?: 'dark' | 'light';
  compactMobile?: boolean;
}) {
  const isMobile = useIsMobile();
  const compact = compactMobile && isMobile === true;
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);

  const cards = (data?.testimonialsCards ?? []).map((card) => ({
    ...card,
    quote:
      (language === 'de' && card.i18n?.de) ||
      (language === 'br' && card.i18n?.br) ||
      card.quote,
  }));
  if (!data?.testimonialsTitle && !cards.length) return null;

  const chunkSize = Math.ceil(cards.length / (compact ? 2 : 3));
  const row1 = cards.slice(0, chunkSize);
  const row2 = cards.slice(chunkSize, chunkSize * 2);
  const row3 = cards.slice(chunkSize * 2);

  const isLight = theme === 'light';

  return (
    <section
      data-home-testimonials={compactMobile || undefined}
      className={cn(
        'relative overflow-x-clip z-10 pb-[15rem] pt-[8rem] lg:py-[25rem] w-full',
        isLight ? 'bg-white' : 'bg-[#001B14]',
        className,
      )}
    >
      {/* Header */}
      <div className={cn("flex flex-col items-center text-center mb-[8rem] px-[2rem] lg:px-0 w-[min(90rem,100%)] mx-auto", compactMobile && "max-lg:mb-[2.75rem]")}>
        {data?.testimonialsChip && (
          <p
            className={cn(
              'font-blacker italic text-[2rem] leading-[1.5] mb-[1rem]',
              isLight ? 'text-black/70' : 'text-white/70',
            )}
          >
            {data.testimonialsChip}
          </p>
        )}
        {data?.testimonialsTitle && (
          <div
            className={cn(
              'richtext [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance]',
              isLight
                ? 'prose-headings:text-black prose-em:text-[#003828] prose-em:not-italic prose-em:font-blacker'
                : 'prose-headings:text-white',
            )}
          >
            <PortableText
              value={data.testimonialsTitle}
              components={richTextComponent}
            />
          </div>
        )}

        <div className={`lg:w-[80rem] w-[50rem] mt-[3rem]`}>
          <TrustpilotMicroCombo
            locale={language === 'de' ? 'de-DE' : 'en-US'}
            theme={isLight ? 'light' : 'dark'}
          />
        </div>
      </div>

      {cards.length > 0 && (
        <div className={cn("flex flex-col gap-[3rem] mb-[10rem]", compactMobile && "max-lg:grid max-lg:auto-rows-fr max-lg:gap-[2.75rem] max-lg:mb-[3.875rem]")}>
          <MarqueeRow
            cards={row1}
            direction={-1}
            scrollVelocity={scrollVelocity}
            theme={theme}
            compactMobile={compactMobile}
          />
          <MarqueeRow
            cards={row2}
            direction={1}
            scrollVelocity={scrollVelocity}
            theme={theme}
            compactMobile={compactMobile}
          />
          {!compact && <MarqueeRow
            cards={row3}
            direction={-1}
            scrollVelocity={scrollVelocity}
            theme={theme}
            compactMobile={compactMobile}
          />}
        </div>
      )}

      {/* Footer: Trustpilot rating + CTA */}
      {(data?.testimonialsSummary || data?.testimonialsButton) && (
        <div className="flex flex-col lg:flex-row items-center lg:items-center justify-between gap-[4rem] w-[min(91rem,calc(100%-4rem))] mx-auto">
          <div className="flex flex-col items-center lg:items-start gap-[1.75rem]">
            {data?.testimonialsSummary && (
              <div
                className={cn(
                  'richtext text-center lg:text-left prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:mb-0 prose-strong:font-semibold',
                  isLight
                    ? 'prose-p:text-black/70 prose-strong:text-black'
                    : 'prose-p:text-white/70 prose-strong:text-white',
                )}
              >
                <PortableText
                  value={data.testimonialsSummary}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
          {data?.testimonialsButton && (
            <div className="flex-shrink-0 w-full lg:w-auto">
              <CTAButton data={data.testimonialsButton} />
            </div>
          )}
        </div>
      )}

      {!isLight && (
        <div className="overflow-hidden absolute inset-x-0 bottom-0 z-50 h-[10rem] lg:block hidden">
          <ScrollingBanner
            theme="dark"
            className="bg-transparent border-y-0"
            words={data.scrollingWords?.words}
            wordsI18n={data.scrollingWords?.wordsI18n}
          />
        </div>
      )}
    </section>
  );
}
